/**
 * Firestore runtime flags for Xero invoices UI (London Smoke & Cure).
 * Doc: config/xero_invoices_ui — Admin can kill-switch without a Hosting rebuild.
 * Does not store OAuth secrets (those stay on Cloud Run / integrations/xero).
 */

import { doc, getDoc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from './firebase';
import { normalizePayNowRules, buildDefaultPayingAccountPayNowRules, normalizeSkuList } from '../utils/payNowOfferRules';

export const XERO_INVOICES_UI_CONFIG_DOC = 'config/xero_invoices_ui';

function clean(v) {
  return v == null ? '' : String(v).trim();
}

export function normalizeXeroInvoicesUiFlags(data = {}) {
  return {
    /** Master stop: all Xero SPA tools off (control panel stays available). */
    killSwitch: data.killSwitch === true,
    /** Account → Invoices / customer Pay surfaces. */
    exposeCustomerTools: data.exposeCustomerTools !== false,
    /** Xero day desk, Admin invoice chips, Raise, Align, etc. */
    exposeAdminTools: data.exposeAdminTools !== false,
    /**
     * Soft Pay now after checkout (and rule evaluator). Default off until Xero day enables.
     */
    payNowOffersEnabled: data.payNowOffersEnabled === true,
    /** Rule rows: AND within row, OR across rows. */
    payNowRules: normalizePayNowRules(data.payNowRules),
    /**
     * Global SKU blocklist (weigh-on-ship etc.) — blocks Submit and pay when present in cart.
     */
    payNowExcludedSkus: normalizeSkuList(data.payNowExcludedSkus),
    updatedAt: data.updatedAt || null,
    updatedByUid: clean(data.updatedByUid) || null,
    updatedByEmail: clean(data.updatedByEmail) || null,
  };
}

export async function fetchXeroInvoicesUiFlags() {
  const snap = await getDoc(doc(db, 'config', 'xero_invoices_ui'));
  if (!snap.exists()) {
    return normalizeXeroInvoicesUiFlags({
      killSwitch: false,
      exposeCustomerTools: true,
      exposeAdminTools: true,
      payNowOffersEnabled: false,
      payNowRules: [],
    });
  }
  return normalizeXeroInvoicesUiFlags(snap.data() || {});
}

/**
 * Subscribe to runtime flags. Callback receives normalized flags.
 * @returns {() => void} unsubscribe
 */
export function subscribeXeroInvoicesUiFlags(onChange, onError) {
  return onSnapshot(
    doc(db, 'config', 'xero_invoices_ui'),
    (snap) => {
      if (!snap.exists()) {
        onChange(
          normalizeXeroInvoicesUiFlags({
            killSwitch: false,
            exposeCustomerTools: true,
            exposeAdminTools: true,
            payNowOffersEnabled: false,
            payNowRules: [],
          })
        );
        return;
      }
      onChange(normalizeXeroInvoicesUiFlags(snap.data() || {}));
    },
    (err) => {
      console.warn('[xeroInvoicesRuntimeFlags] subscribe failed', err);
      onError?.(err);
    }
  );
}

/**
 * Merge-write UI flags (Admin). Never deletes fields.
 * When payNowRules is provided, the full array is written (replace list, not sparse delete of doc).
 */
export async function saveXeroInvoicesUiFlags(patch = {}) {
  const user = auth.currentUser;
  const payload = {
    updatedAt: serverTimestamp(),
    updatedByUid: user?.uid || null,
    updatedByEmail: user?.email || null,
  };
  if (Object.prototype.hasOwnProperty.call(patch, 'killSwitch')) {
    payload.killSwitch = patch.killSwitch === true;
  }
  if (Object.prototype.hasOwnProperty.call(patch, 'exposeCustomerTools')) {
    payload.exposeCustomerTools = patch.exposeCustomerTools === true;
  }
  if (Object.prototype.hasOwnProperty.call(patch, 'exposeAdminTools')) {
    payload.exposeAdminTools = patch.exposeAdminTools === true;
  }
  if (Object.prototype.hasOwnProperty.call(patch, 'payNowOffersEnabled')) {
    payload.payNowOffersEnabled = patch.payNowOffersEnabled === true;
  }
  if (Object.prototype.hasOwnProperty.call(patch, 'payNowRules')) {
    payload.payNowRules = normalizePayNowRules(patch.payNowRules);
  }
  if (Object.prototype.hasOwnProperty.call(patch, 'payNowExcludedSkus')) {
    payload.payNowExcludedSkus = normalizeSkuList(patch.payNowExcludedSkus);
  }
  await setDoc(doc(db, 'config', 'xero_invoices_ui'), payload, { merge: true });
  return fetchXeroInvoicesUiFlags();
}

/**
 * One-shot Admin enable: customer Pay now surfaces + soft checkout offers for Must pay / No credit.
 * Keeps existing payNowRules when any are already saved.
 */
export async function enableCustomerPayNowCheckoutFlags() {
  const current = await fetchXeroInvoicesUiFlags();
  const hasRules = Array.isArray(current.payNowRules) && current.payNowRules.length > 0;
  return saveXeroInvoicesUiFlags({
    killSwitch: false,
    exposeCustomerTools: true,
    exposeAdminTools: true,
    payNowOffersEnabled: true,
    payNowRules: hasRules ? current.payNowRules : buildDefaultPayingAccountPayNowRules(),
  });
}
